import type { AssetBounds, AssetType } from './types.js';

/** Union of three variants at 4x raster sampling, rounded outward with motion padding. */
export const ASSET_BOUNDS: Readonly<Record<AssetType, AssetBounds>> = {
  cedarGrove: { x: -8, y: -17, width: 16, height: 19 },
  ancientOak: { x: -8, y: -18, width: 16, height: 20 },
  wildflowerMeadow: { x: -8, y: -10, width: 16, height: 12 },
  bambooThicket: { x: -8, y: -17, width: 16, height: 19 },
  lotusPond: { x: -8, y: -8, width: 16, height: 11 },
  reedMarsh: { x: -8, y: -13, width: 16, height: 16 },
  alpineRocks: { x: -8, y: -12, width: 16, height: 14 },
  willowPond: { x: -8, y: -14, width: 16, height: 17 },
  whale: { x: -5, y: -6, width: 13, height: 9 },
  fish: { x: -4, y: -4, width: 9, height: 6 },
  fishSchool: { x: -3, y: -3, width: 6, height: 5 },
  boat: { x: -4, y: -7, width: 10, height: 8 },
  seagull: { x: -8, y: -11, width: 17, height: 16 },
  dock: { x: -4, y: -2, width: 8, height: 5 },
  waves: { x: -5, y: -3, width: 10, height: 5 },
  kelp: { x: -2, y: -9, width: 5, height: 11 },
  coral: { x: -4, y: -6, width: 8, height: 8 },
  jellyfish: { x: -3, y: -5, width: 6, height: 6 },
  turtle: { x: -8, y: -8, width: 15, height: 14 },
  buoy: { x: -3, y: -5, width: 6, height: 7 },
  sailboat: { x: -5, y: -8, width: 10, height: 9 },
  lighthouse: { x: -3, y: -12, width: 6, height: 13 },
  crab: { x: -4, y: -4, width: 8, height: 6 },
  rock: { x: -4, y: -4, width: 8, height: 6 },
  boulder: { x: -4, y: -5, width: 8, height: 7 },
  flower: { x: -3, y: -5, width: 6, height: 6 },
  bush: { x: -5, y: -4, width: 10, height: 6 },
  driftwood: { x: -5, y: -3, width: 10, height: 5 },
  sandcastle: { x: -3, y: -6, width: 6, height: 7 },
  tidePools: { x: -4, y: -2, width: 8, height: 4 },
  heron: { x: -3, y: -8, width: 6, height: 9 },
  shellfish: { x: -3, y: -2, width: 7, height: 4 },
  cattail: { x: -2, y: -8, width: 4, height: 9 },
  frog: { x: -2, y: -3, width: 4, height: 5 },
  lily: { x: -3, y: -3, width: 6, height: 5 },
  pine: { x: -5, y: -13, width: 10, height: 15 },
  deciduous: { x: -6, y: -13, width: 12, height: 15 },
  mushroom: { x: -3, y: -5, width: 6, height: 6 },
  stump: { x: -3, y: -4, width: 6, height: 5 },
  deer: { x: -5, y: -7, width: 9, height: 9 },
  rabbit: { x: -3, y: -4, width: 7, height: 6 },
  fox: { x: -4, y: -5, width: 9, height: 7 },
  butterfly: { x: -7, y: -10, width: 14, height: 13 },
  beehive: { x: -3, y: -8, width: 6, height: 8 },
  wildflowerPatch: { x: -3, y: -4, width: 6, height: 5 },
  tallGrass: { x: -3, y: -5, width: 6, height: 6 },
  birch: { x: -5, y: -12, width: 10, height: 14 },
  haybale: { x: -3, y: -3, width: 6, height: 5 },
  willow: { x: -6, y: -9, width: 12, height: 11 },
  palm: { x: -6, y: -11, width: 12, height: 13 },
  bird: { x: -7, y: -12, width: 16, height: 15 },
  owl: { x: -3, y: -5, width: 6, height: 6 },
  squirrel: { x: -2, y: -4, width: 5, height: 5 },
  moss: { x: -4, y: -2, width: 8, height: 4 },
  fern: { x: -5, y: -5, width: 10, height: 7 },
  deadTree: { x: -4, y: -8, width: 7, height: 9 },
  log: { x: -5, y: -2, width: 10, height: 3 },
  berryBush: { x: -4, y: -4, width: 8, height: 5 },
  spider: { x: -4, y: -6, width: 8, height: 7 },
  wheat: { x: -4, y: -7, width: 8, height: 8 },
  fence: { x: -5, y: -5, width: 10, height: 6 },
  scarecrow: { x: -4, y: -10, width: 9, height: 11 },
  barn: { x: -6, y: -9, width: 12, height: 12 },
  sheep: { x: -4, y: -5, width: 8, height: 7 },
  cow: { x: -5, y: -6, width: 10, height: 8 },
  chicken: { x: -3, y: -5, width: 8, height: 7 },
  horse: { x: -4, y: -9, width: 10, height: 11 },
  donkey: { x: -4, y: -7, width: 8, height: 9 },
  goat: { x: -4, y: -6, width: 8, height: 8 },
  ricePaddy: { x: -6, y: -5, width: 12, height: 8 },
  silo: { x: -3, y: -10, width: 6, height: 11 },
  pigpen: { x: -4, y: -3, width: 8, height: 4 },
  trough: { x: -3, y: -2, width: 6, height: 4 },
  haystack: { x: -3, y: -6, width: 6, height: 7 },
  orchard: { x: -4, y: -9, width: 8, height: 10 },
  appleTree: { x: -4, y: -8, width: 8, height: 11 },
  oliveTree: { x: -4, y: -7, width: 8, height: 11 },
  lemonTree: { x: -4, y: -7, width: 8, height: 10 },
  orangeTree: { x: -4, y: -8, width: 8, height: 11 },
  pearTree: { x: -4, y: -8, width: 8, height: 11 },
  peachTree: { x: -4, y: -8, width: 8, height: 11 },
  beeFarm: { x: -3, y: -5, width: 6, height: 6 },
  pumpkin: { x: -3, y: -4, width: 6, height: 6 },
  tent: { x: -5, y: -8, width: 10, height: 9 },
  hut: { x: -4, y: -7, width: 10, height: 8 },
  house: { x: -5, y: -8, width: 10, height: 11 },
  houseB: { x: -5, y: -8, width: 10, height: 11 },
  church: { x: -4, y: -13, width: 8, height: 15 },
  windmill: { x: -5, y: -12, width: 10, height: 13 },
  well: { x: -4, y: -7, width: 8, height: 9 },
  tavern: { x: -4, y: -7, width: 10, height: 10 },
  bakery: { x: -4, y: -10, width: 8, height: 11 },
  stable: { x: -5, y: -6, width: 10, height: 7 },
  garden: { x: -5, y: -3, width: 10, height: 4 },
  laundry: { x: -5, y: -5, width: 10, height: 6 },
  doghouse: { x: -3, y: -5, width: 8, height: 7 },
  shrine: { x: -3, y: -6, width: 6, height: 7 },
  wagon: { x: -5, y: -6, width: 9, height: 8 },
  market: { x: -5, y: -7, width: 13, height: 9 },
  inn: { x: -5, y: -8, width: 12, height: 11 },
  blacksmith: { x: -4, y: -8, width: 8, height: 9 },
  castle: { x: -4, y: -13, width: 8, height: 14 },
  tower: { x: -3, y: -12, width: 6, height: 13 },
  bridge: { x: -6, y: -4, width: 12, height: 6 },
  cathedral: { x: -5, y: -13, width: 10, height: 16 },
  library: { x: -4, y: -7, width: 8, height: 8 },
  clocktower: { x: -3, y: -14, width: 6, height: 15 },
  statue: { x: -2, y: -7, width: 4, height: 8 },
  park: { x: -5, y: -8, width: 10, height: 9 },
  warehouse: { x: -5, y: -7, width: 10, height: 8 },
  gatehouse: { x: -4, y: -8, width: 8, height: 9 },
  manor: { x: -5, y: -9, width: 10, height: 12 },
  reeds: { x: -3, y: -7, width: 6, height: 8 },
  fountain: { x: -4, y: -6, width: 8, height: 8 },
  canal: { x: -4, y: -2, width: 8, height: 3 },
  watermill: { x: -4, y: -7, width: 11, height: 9 },
  gardenTree: { x: -3, y: -8, width: 6, height: 9 },
  pondLily: { x: -3, y: -2, width: 6, height: 4 },
  cart: { x: -4, y: -3, width: 9, height: 5 },
  barrel: { x: -3, y: -5, width: 6, height: 7 },
  torch: { x: -2, y: -7, width: 5, height: 9 },
  flag: { x: -2, y: -10, width: 7, height: 11 },
  cobblePath: { x: -4, y: -3, width: 9, height: 5 },
  smoke: { x: -5, y: -11, width: 11, height: 11 },
  signpost: { x: -4, y: -6, width: 8, height: 7 },
  lantern: { x: -2, y: -7, width: 4, height: 8 },
  woodpile: { x: -4, y: -4, width: 8, height: 5 },
  puddle: { x: -3, y: -2, width: 6, height: 4 },
  campfire: { x: -3, y: -4, width: 6, height: 6 },
  snowPine: { x: -5, y: -10, width: 10, height: 13 },
  snowDeciduous: { x: -5, y: -7, width: 10, height: 11 },
  snowman: { x: -4, y: -8, width: 9, height: 12 },
  snowdrift: { x: -5, y: -3, width: 10, height: 6 },
  igloo: { x: -5, y: -5, width: 10, height: 7 },
  frozenPond: { x: -4, y: -3, width: 8, height: 6 },
  icicle: { x: -3, y: -5, width: 6, height: 6 },
  sled: { x: -5, y: -4, width: 10, height: 6 },
  snowCoveredRock: { x: -4, y: -3, width: 8, height: 6 },
  bareBush: { x: -4, y: -4, width: 8, height: 6 },
  winterBird: { x: -3, y: -4, width: 6, height: 5 },
  firewood: { x: -4, y: -4, width: 8, height: 6 },
  houseWinter: { x: -5, y: -9, width: 10, height: 12 },
  houseBWinter: { x: -5, y: -9, width: 10, height: 12 },
  barnWinter: { x: -6, y: -9, width: 12, height: 12 },
  churchWinter: { x: -4, y: -13, width: 8, height: 15 },
  christmasTree: { x: -4, y: -10, width: 8, height: 13 },
  winterLantern: { x: -4, y: -8, width: 8, height: 10 },
  frozenFountain: { x: -4, y: -6, width: 8, height: 8 },
  cherryBlossom: { x: -5, y: -7, width: 10, height: 10 },
  cherryBlossomSmall: { x: -3, y: -5, width: 6, height: 8 },
  cherryPetals: { x: -3, y: -3, width: 6, height: 5 },
  tulip: { x: -2, y: -4, width: 5, height: 6 },
  tulipField: { x: -4, y: -4, width: 8, height: 6 },
  sprout: { x: -2, y: -3, width: 4, height: 5 },
  nest: { x: -3, y: -3, width: 6, height: 5 },
  lamb: { x: -4, y: -4, width: 9, height: 7 },
  crocus: { x: -2, y: -3, width: 4, height: 5 },
  rainPuddle: { x: -3, y: -2, width: 6, height: 4 },
  birdhouse: { x: -3, y: -6, width: 6, height: 8 },
  gardenBed: { x: -4, y: -3, width: 8, height: 5 },
  cherryBlossomFull: { x: -5, y: -9, width: 10, height: 13 },
  cherryBlossomBranch: { x: -5, y: -5, width: 8, height: 7 },
  peachBlossom: { x: -4, y: -8, width: 8, height: 12 },
  flowerBed: { x: -3, y: -3, width: 6, height: 5 },
  wateringCan: { x: -2, y: -4, width: 6, height: 6 },
  seedling: { x: -2, y: -3, width: 4, height: 5 },
  robinBird: { x: -2, y: -2, width: 5, height: 4 },
  butterflyGarden: { x: -3, y: -3, width: 6, height: 4 },
  umbrella: { x: -3, y: -4, width: 6, height: 7 },
  parasol: { x: -4, y: -6, width: 8, height: 8 },
  beachTowel: { x: -4, y: -2, width: 8, height: 5 },
  sandcastleSummer: { x: -3, y: -5, width: 6, height: 7 },
  surfboard: { x: -3, y: -6, width: 6, height: 8 },
  iceCreamCart: { x: -3, y: -5, width: 7, height: 8 },
  hammock: { x: -5, y: -4, width: 10, height: 5 },
  sunflower: { x: -5, y: -7, width: 10, height: 9 },
  watermelon: { x: -3, y: -2, width: 6, height: 4 },
  sprinkler: { x: -3, y: -4, width: 6, height: 6 },
  lemonade: { x: -4, y: -3, width: 8, height: 5 },
  fireflies: { x: -6, y: -5, width: 11, height: 6 },
  swimmingPool: { x: -4, y: -4, width: 8, height: 7 },
  autumnMaple: { x: -4, y: -8, width: 8, height: 12 },
  autumnOak: { x: -4, y: -8, width: 8, height: 12 },
  autumnBirch: { x: -4, y: -6, width: 7, height: 10 },
  autumnGinkgo: { x: -4, y: -7, width: 8, height: 10 },
  fallenLeaves: { x: -4, y: -2, width: 6, height: 4 },
  leafSwirl: { x: -3, y: -4, width: 6, height: 5 },
  acorn: { x: -2, y: -2, width: 4, height: 4 },
  cornStalk: { x: -4, y: -4, width: 8, height: 6 },
  scarecrowAutumn: { x: -3, y: -6, width: 7, height: 8 },
  harvestBasket: { x: -3, y: -3, width: 6, height: 4 },
  hotDrink: { x: -2, y: -4, width: 5, height: 6 },
  autumnWreath: { x: -3, y: -5, width: 6, height: 7 },
  pumpkinPatch: { x: -3, y: -3, width: 6, height: 5 },
  hayMaze: { x: -3, y: -3, width: 6, height: 5 },
  appleBasket: { x: -3, y: -3, width: 6, height: 5 },
  rake: { x: -4, y: -5, width: 8, height: 8 },
  hanok: { x: -10, y: -15, width: 20, height: 19 },
  pavilion: { x: -9, y: -15, width: 18, height: 19 },
  stoneWall: { x: -9, y: -8, width: 18, height: 15 },
  onggi: { x: -7, y: -8, width: 14, height: 12 },
  choga: { x: -10, y: -13, width: 20, height: 17 },
  jangseung: { x: -7, y: -16, width: 14, height: 19 },
  sotdae: { x: -8, y: -17, width: 16, height: 20 },
  riceTerrace: { x: -10, y: -8, width: 20, height: 15 },
  koreanWatermill: { x: -12, y: -15, width: 24, height: 21 },
  hanokGate: { x: -11, y: -17, width: 22, height: 22 },
  kimchiGarden: { x: -9, y: -8, width: 18, height: 13 },
  stoneBridge: { x: -11, y: -11, width: 22, height: 17 },
  hanokEstate: { x: -12, y: -20, width: 24, height: 25 },
};
